import { Component, OnChanges, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { IOptionGroup, IOption, optionsDefaultClassName } from './../../../api/models/offerings';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

/**
 * This is a general component that can be used to display groups of options, such as Offerings or Services
 *
 * @Input title that will be displayed in the top of the component
 * @Input subtitle that will be displayed below the title
 * @Input content group of options to be displayed. For each group of options, a collapsable component is displayed
 * @Input showSearchBard sets the flag that displays or not the Search Bar
 * @Input searchLabel label for the Search bar
 * @Input isCollapsable If true the group of options will be organized in collapsable blocks. If false the groups of options will be always visible
 * @Ouput storeOpeningTimesEmitter Emits an array with selected options
 */
@Component({
  selector: 'generic-attribute-selection',
  templateUrl: './offerings.component.html',
  styleUrls: ['./offerings.component.scss'],
  standalone: false,
})
export class OfferingsComponent implements OnChanges, OnInit, OnDestroy {
  @Output() searchOutput = new EventEmitter<string>();
  @Output() selectedOptions = new EventEmitter<string[]>();
  @Input() title: string;
  @Input() subtitle: string;
  @Input() content: IOptionGroup[];
  @Input() showSearchBar: boolean;
  @Input() searchText: string;
  @Input() isCollapsable: boolean;
  @Input() optionsClassName: string = optionsDefaultClassName;
  @Input() withBorder: boolean;
  searchControl = new FormControl('');
  searchSubscription$ = new Subject<void>();
  selectedOptionsArray: string[] = [];
  searchMode = false;

  ngOnInit(): void {
    if (this.showSearchBar) {
      this.searchControl.valueChanges.pipe(takeUntil(this.searchSubscription$), debounceTime(500), distinctUntilChanged()).subscribe(search => {
        this.searchOutput.emit(search);
        this.searchMode = !!search;
      });
    }
  }

  ngOnChanges(): void {
    if (this.content) {
      this.setSelectedOptions();
    }
  }

  onOptionsChange(item: IOption) {
    item.selected = !item.selected;
    const { selected, name } = item;
    if (selected) {
      this.selectedOptionsArray.push(name);
    } else {
      this.selectedOptionsArray = this.selectedOptionsArray.filter(option => option !== name);
    }
    this.selectedOptions.emit(this.selectedOptionsArray);
  }

  /**
   * Opens/closes the selected collapsable panel.
   * Other panels keep their state to avoid layout jumps above the clicked section.
   */
  openCollapsable(collapsableIndex: number): void {
    if (this.isCollapsable) {
      const group = this.content[collapsableIndex];
      group.isOpen = !group.isOpen;
    }
  }

  /**
   * Sets the storeOpeningTimesEmitter array when the component initializes.
   * For each group of options, checks which options are selected and pushs its name to the selectedOptionsArray
   */
  private setSelectedOptions(): void {
    this.selectedOptionsArray = [];
    this.content.forEach((group: IOptionGroup) => {
      const selectedOptions = group.options.filter((option: IOption) => option.selected).map((selectedOptions: IOption) => selectedOptions.name);
      this.selectedOptionsArray = this.selectedOptionsArray.concat(selectedOptions);
    });
  }

  /**
   * Clears the search
   */
  clearSearch(): void {
    this.searchControl.setValue('');
  }

  ngOnDestroy(): void {
    this.searchSubscription$.unsubscribe();
  }
}
